import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  useDisclosure,
  Box,
  Card,
  CardHeader,
  HStack,
  Heading,
  useToast,
  FormControl,
  FormLabel,
  Switch,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { WSStore } from "../../../services/remote/store/WSStore";
import { setStore } from "../../../store/slices/StoreSlice";
import { Store } from "../../../entity/Store";
import { selectStore } from "../../../store/selectors/StoreSelector";

import StoreFormModal from "./componenets/CreateStoreModal";
import { setLoading } from "../../../store/slices/LoadingSlice";
import Dialog from "../../components/Dialog";
import { StoresTable } from "./componenets/SotredTable";
import MigrationDialog from "./componenets/MigrationModel";

// Main Component
export const StoreManagement = () => {
  const { t } = useTranslation();
  const currentStore = useSelector(selectStore);
  const dispatch = useDispatch();
  const [stores, setStores] = useState<Store[]>([]);

  const toast = useToast();
  const storeInstance = WSStore.getInstance();
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [showAllStores, setShowAllStores] = useState(false);
  const [openDialoge, setOpenDialoge] = useState(false);
  const [openDeletePermanently, setOpenDeletePermanently] = useState(false);

  const [openMigrationDialoge, setOpenMigrationDialoge] = useState(false);

  const [storeId, setStoreId] = useState<string>("");
  const loadStores = async () => {
    try {
      const stores = showAllStores
        ? await storeInstance.getAllSTORE()
        : await storeInstance.getAllEnaledSTORE();
      setStores(stores);
    } catch (error) {
      console.error("Failed to load stores:", error);
      toast({
        title: t("store.loadError"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    dispatch(setLoading(true));
    loadStores();
    dispatch(setLoading(false));
  }, [t, toast, showAllStores]);

  const handleStoreCreated = (newStore: Store) => {
    setStores((prev) => [...prev, newStore]);
    dispatch(setStore(newStore));
  };
  const handleStoreUpdated = (updatedStore: Store) => {
    setStores((prev) =>
      prev.map((store) =>
        store.id === updatedStore.id ? updatedStore : store,
      ),
    );
    if (currentStore?.id === updatedStore.id) {
      dispatch(setStore(updatedStore));
    }
  };
  const handleEdit = (store: Store) => {
    setSelectedStore(store);
    onOpen();
  };

  const handleMigratation = async (storeId: string) => {
    setStoreId(storeId);
    setOpenMigrationDialoge(true);
  };
  const handleDeleteClick = async (storeId: string) => {
    setStoreId(storeId);
    setOpenDialoge(true);
  };

  const handleDeletePermanentlyClick = async (storeId: string) => {
    setOpenDeletePermanently(true);
    setStoreId(storeId);
  };

  /**
   * This function just disabled the store, not deleted permanently
   */
  const handleDelete = async () => {
    try {
      const storeInstance = WSStore.getInstance();
      await storeInstance.deleteStore(storeId);
      setStores((prev) => prev.filter((store) => store.id !== storeId));
      setOpenDialoge(false);
      if (currentStore?.id === storeId) {
        dispatch(setStore(null));
      }
      toast({
        title: "Store deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Failed to delete store:", error);
      toast({
        title: "Failed to delete store",
        description: error instanceof Error ? error.message : "Unknown error",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  /*
   * Delete deleteStorePermanently store
   */

  const handleDeletePermanently = async () => {
    try {
      const storeInstance = WSStore.getInstance();
      await storeInstance.deleteStorePermanently(storeId);
      setStores((prev) => prev.filter((store) => store.id !== storeId));
      setOpenDialoge(false);
      if (currentStore?.id === storeId) {
        dispatch(setStore(null));
      }
      toast({
        title: "Store deleted permanently",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Failed to delete store permanently:", error);
      toast({
        title: "Failed to delete store permanently",
        description: error instanceof Error ? error.message : "Unknown error",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setOpenDeletePermanently(false);
    }
  };
  return (
    <Box p={4}>
      <Card variant="outline" mb={6}>
        <CardHeader>
          <HStack justify="space-between" align="center">
            <Heading size="md">{t("store.manageStores")}</Heading>
            <HStack spacing={4}>
              <FormControl display="flex" alignItems="center" width="auto">
                <FormLabel htmlFor="show-all-stores" mb="0" whiteSpace="nowrap">
                  {t("store.showAllStores")}
                </FormLabel>
                <Switch
                  id="show-all-stores"
                  isChecked={showAllStores}
                  onChange={(e) => setShowAllStores(e.target.checked)}
                />
              </FormControl>
              <Button onClick={onOpen}>{t("store.createStore")}</Button>
            </HStack>
          </HStack>
        </CardHeader>
        <Box p={4}>
          <StoresTable
            stores={stores}
            onEdit={handleEdit}
            onMigrate={handleMigratation}
            onDelete={handleDeleteClick}
            onDeletePermanently={handleDeletePermanentlyClick}
            isShowStatus={showAllStores}
          />
        </Box>
      </Card>

      <StoreFormModal
        isOpen={isOpen}
        onClose={() => {
          setSelectedStore(null);
          onClose();
        }}
        onSuccess={selectedStore ? handleStoreUpdated : handleStoreCreated}
        storeToEdit={selectedStore}
      />
      <Dialog
        onClick={handleDelete}
        open={openDialoge}
        onClose={() => setOpenDialoge(false)}
        title={t("popup.txt_delete_store")}
        description={t("popup.are_you_sure_delete_store")}
        positiveButtonText={t("popup.txt_delete")}
        negativeButtonText={t("popup.txt_cancel")}
      />

      <MigrationDialog
        storeId={storeId}
        isOpen={openMigrationDialoge}
        onClose={() => setOpenMigrationDialoge(false)}
        onSuccess={() => {
          loadStores();
        }}
      />
      <Dialog
        onClick={handleDeletePermanently}
        open={openDeletePermanently}
        onClose={() => setOpenDeletePermanently(false)}
        title={t("popup.txt_delete_store_permanently")}
        description={t("popup.are_you_sure_delete_store_permanently")}
        positiveButtonText={t("popup.txt_delete")}
        negativeButtonText={t("popup.txt_cancel")}
      />
    </Box>
  );
};
